import { useCallback, useEffect, useState } from 'react';
import { App, Button, Empty, Input, Modal, Select, Space, Tag } from 'antd';
import { MailOutlined, PlusOutlined } from '@ant-design/icons';
import type { Rfi, RfiStatus } from '@/api/types';
import { closeRfi, listRfi } from '@/api/rfi';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { formatDay } from '@/lib/time';
import { RfiComposer } from './RfiComposer';
import { erasedOr } from '@/lib/erased';

const CLOSE_OUTCOMES = [
  { value: 'SATISFIED', label: 'Satisfied — response sufficient' },
  { value: 'PARTIAL', label: 'Partial — response insufficient' },
  { value: 'NO_RESPONSE', label: 'No response' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const OPEN_STATUSES = ['DRAFT', 'SENT', 'RESPONDED'];

function RfiStatusTag({ status }: { status: RfiStatus }) {
  const s = String(status).toUpperCase();
  const color =
    s === 'SENT'
      ? 'processing'
      : s === 'RESPONDED'
        ? 'blue'
        : s === 'CLOSED'
          ? 'success'
          : s === 'EXPIRED'
            ? 'warning'
            : s === 'CANCELLED'
              ? 'default'
              : 'default';
  return (
    <Tag bordered={false} color={color}>
      {status}
    </Tag>
  );
}

/** RFIs raised for an entity (the entity-360 RFI tab). Compose new, review status, close out. */
export function RfiListPanel({ entityId }: { entityId: string }) {
  const { message } = App.useApp();
  const [rfis, setRfis] = useState<Rfi[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  const [closeTarget, setCloseTarget] = useState<Rfi | null>(null);
  const [closeOutcome, setCloseOutcome] = useState<string>('SATISFIED');
  const [closeNote, setCloseNote] = useState('');
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      setRfis(await listRfi(entityId));
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setRfis([]);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function doClose() {
    if (!closeTarget) return;
    setClosing(true);
    try {
      await closeRfi(closeTarget.rfiId, { outcome: closeOutcome, note: closeNote.trim() || undefined });
      message.success('RFI closed.');
      setCloseTarget(null);
      setCloseNote('');
      void load();
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) message.info('The RFI endpoint is not live yet (Track A pending).');
      else message.error(n.message);
    } finally {
      setClosing(false);
    }
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <div className="ol-section" style={{ margin: 0 }}>
        <MailOutlined /> Requests for information
        {rfis.length > 0 && <span className="ol-section__count">· {rfis.length}</span>}
      </div>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposerOpen(true)}>
        Raise RFI
      </Button>
    </div>
  );

  return (
    <div>
      {header}

      {unavailable ? (
        <EmptyState
          icon={<MailOutlined />}
          title="RFI outreach"
          wave="PS-3"
          description="Requests for information raised for this entity appear here once the RFI API is live (Track A). Compose is available now — the deterministic draft is assembled client-side; the two-step gate is enforced in the UI."
          bullets={[
            'Asks with semantic-gap precision ("this is a 2019 statement")',
            'Channel + locale selection; deterministic client-side draft (no AI)',
            'Two-step human gate: compose → review-and-send',
            'Status tracking and close-out with an outcome',
          ]}
          action={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setComposerOpen(true)}>
              Compose RFI
            </Button>
          }
        />
      ) : loading ? (
        <div style={{ color: 'var(--ol-text-tertiary)', fontSize: 13 }}>Loading…</div>
      ) : rfis.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No RFIs raised for this entity yet." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rfis.map((r) => (
            <div className="ol-card" key={r.rfiId}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {erasedOr(r.subject ?? 'Request for information')}
                </span>
                <RfiStatusTag status={r.status} />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', marginTop: 5 }}>
                {r.channel} · {r.locale} · {r.asks.length} ask(s)
                {r.createdAt ? ` · raised ${formatDay(r.createdAt)}` : ''}
                {r.sentAt ? ` · sent ${formatDay(r.sentAt)}` : ''}
                {r.outcome ? ` · outcome ${r.outcome}` : ''}
              </div>
              {OPEN_STATUSES.includes(String(r.status).toUpperCase()) && (
                <div style={{ marginTop: 8 }}>
                  <Button size="small" onClick={() => setCloseTarget(r)}>
                    Close out
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <RfiComposer
        open={composerOpen}
        entityId={entityId}
        onClose={() => setComposerOpen(false)}
        onChanged={() => {
          setComposerOpen(false);
          void load();
        }}
      />

      <Modal
        open={Boolean(closeTarget)}
        title="Close RFI"
        onCancel={() => setCloseTarget(null)}
        onOk={doClose}
        okText="Close RFI"
        confirmLoading={closing}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            value={closeOutcome}
            onChange={setCloseOutcome}
            options={CLOSE_OUTCOMES}
            style={{ width: '100%' }}
          />
          <Input.TextArea
            placeholder="Close-out note (optional)"
            autoSize={{ minRows: 2, maxRows: 4 }}
            value={closeNote}
            onChange={(e) => setCloseNote(e.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
}
